import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Button, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useRecoilValue, useRecoilState, useSetRecoilState } from 'recoil';
import { atom_modalType, atom_showModalContent } from '@app/shared/state/show-content-modal.atom';
import { atom_dropPinMode } from '@app/shared/state/drop-pin-mode.atom';
import { atom_tourState } from '@app/shared/state/tour.atom';
import { atom_userAddedPlaces, atom_selectedUserPlace, PlaceDetails } from '@app/shared/state/user-added-stops.atom';
import { atom_mapRef } from '@app/shared/state/map-ref.atom';
import { PlaceSearchInline } from '@app/shared/components/add-a-place/PlaceSearchInline';
import { service_tourService } from '@app/shared/services/tours.service';


const THEME_COLOR = '#D0552E';
const MAX_DESCRIPTION_LENGTH = 100;

const DesktopDropAPinContent: React.FC = () => {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.language === 'he';
    const setState_modalType = useSetRecoilState(atom_modalType);
    const setState_showModal = useSetRecoilState(atom_showModalContent);
    const [dropPinMode, setDropPinMode] = useRecoilState(atom_dropPinMode);
    const activeTour = useRecoilValue(atom_tourState);
    const [userAddedPlaces, setUserAddedPlaces] = useRecoilState(atom_userAddedPlaces);
    const [selectedUserPlace, setSelectedUserPlace] = useRecoilState(atom_selectedUserPlace);
    const map = useRecoilValue(atom_mapRef);

    // Form state for naming step
    const [placeName, setPlaceName] = useState('');
    const [description, setDescription] = useState('');
    const [tags, setTags] = useState<string[]>([]);
    const [placeType, setPlaceType] = useState<'dropped_pin' | 'search_place'>('dropped_pin');
    const didSaveRef = useRef(false);

    // On unmount (modal dismissed without save): clean up temp or sync existing
    useEffect(() => {
        return () => {
            if (didSaveRef.current) return; // save/delete already handled
            const tempId = dropPinMode.tempPlaceId;
            const tourId = activeTour?.id;
            if (!tempId || !tourId) return;

            const existingPlace = (userAddedPlaces[tourId] || []).find((p: any) => p.place_id === tempId);
            const isEditingExisting = existingPlace && (existingPlace as any).updatedAt;
            if (isEditingExisting) {
                // Existing place — save updated location to localStorage + backend
                const place = (userAddedPlaces[tourId] || []).find((p: any) => p.place_id === tempId);
                localStorage.setItem('userAddedPlaces', JSON.stringify(userAddedPlaces));
                if (place) {
                    const { uID } = JSON.parse(localStorage.getItem('credential_items') || '{}');
                    if (uID) {
                        service_tourService.saveUserAddedStop(uID, tourId, place);
                    }
                }
            } else {
                // New temp place — remove it
                const currentPlaces = userAddedPlaces[tourId] || [];
                const filtered = currentPlaces.filter((p: any) => p.place_id !== tempId);
                const updatedPlaces = { ...userAddedPlaces, [tourId]: filtered };
                setUserAddedPlaces(updatedPlaces);
                localStorage.setItem('userAddedPlaces', JSON.stringify(updatedPlaces));
            }
        };
    }, []);

    // Load existing place data on mount if available
    React.useEffect(() => {
        if (selectedUserPlace) {
            setPlaceName(selectedUserPlace.name || '');
            setDescription(selectedUserPlace.description || '');
            setTags(selectedUserPlace.tags || []);
            setPlaceType(selectedUserPlace.placeType === 'search_place' ? 'search_place' : 'dropped_pin');
        }
    }, [selectedUserPlace]);

    // Pin location from drop pin mode
    const pinLocation = dropPinMode.pinLocation;

    // Load search name from drop pin mode if available
    React.useEffect(() => {
        if (dropPinMode.searchName && !selectedUserPlace) {
            setPlaceName(dropPinMode.searchName);
            setPlaceType('search_place');
        }
    }, [dropPinMode.searchName, selectedUserPlace]);

    const handleEditLocation = () => {
        didSaveRef.current = true; // prevent unmount cleanup
        // Track which place to hide during positioning (works for both temp and saved places)
        const placeId = selectedUserPlace?.place_id || dropPinMode.tempPlaceId || null;
        // Go back to positioning mode
        setState_modalType(null);
        setState_showModal(false);
        setDropPinMode({
            isActive: true,
            step: 'positioning',
            pinLocation: dropPinMode.pinLocation,
            tempPlaceId: placeId,
        });
    };

    // Handle search place selected - update pin location and name from search result
    const handleSearchPlaceSelected = (details: PlaceDetails) => {
        setDropPinMode({
            ...dropPinMode,
            pinLocation: {
                lat: details.geometry.location.lat,
                lng: details.geometry.location.lng,
            },
        });
        setPlaceName(details.name || '');
        setPlaceType('search_place');

        // Fly map to searched location
        if (map) {
            map.flyTo([details.geometry.location.lat, details.geometry.location.lng], 17, { duration: 1.5 });
        }
    };

    // Check if editing an existing place
    const isEditing = selectedUserPlace && selectedUserPlace.place_id;

    const handleDeletePlace = () => {
        didSaveRef.current = true;
        if (!activeTour?.id || !selectedUserPlace?.place_id) return;

        const tourId = activeTour.id;
        const currentTourPlaces = userAddedPlaces[tourId] || [];

        const updatedPlaces = {
            ...userAddedPlaces,
            [tourId]: currentTourPlaces.filter((p: any) => p.place_id !== selectedUserPlace.place_id),
        };

        setUserAddedPlaces(updatedPlaces);
        localStorage.setItem('userAddedPlaces', JSON.stringify(updatedPlaces));

        // Close modal
        setState_modalType(null);
        setState_showModal(false);
        setDropPinMode({ isActive: false, step: null, pinLocation: null });
        setSelectedUserPlace(null);
    };

    const handleSavePlace = () => {
        didSaveRef.current = true;
        if (!pinLocation || !activeTour?.id || !placeName.trim()) return;

        const tourId = activeTour.id;
        const currentTourPlaces = userAddedPlaces[tourId] || [];

        const placeToSave = {
            place_id: isEditing ? selectedUserPlace.place_id : `dropped_pin_${Date.now()}`,
            name: placeName.trim(),
            description: description.trim(),
            // formatted_address: `${pinLocation.lat.toFixed(6)}, ${pinLocation.lng.toFixed(6)}`,
            geometry: {
                location: {
                    lat: pinLocation.lat,
                    lng: pinLocation.lng,
                },
            },
            icon: 'user',
            tags: tags,
            placeType: placeType,
        };

        const updatedPlaces = isEditing
            ? {
                ...userAddedPlaces,
                [tourId]: currentTourPlaces.map((p: any) =>
                    p.place_id === selectedUserPlace.place_id ? placeToSave : p
                ),
            }
            : {
                ...userAddedPlaces,
                [tourId]: [...currentTourPlaces, placeToSave],
            };

        setUserAddedPlaces(updatedPlaces);
        localStorage.setItem('userAddedPlaces', JSON.stringify(updatedPlaces));

        // Close modal - cleanup handled by AccessibleModal's onClose
        setState_modalType(null);
        setState_showModal(false);
        setDropPinMode({ isActive: false, step: null, pinLocation: null });
        setSelectedUserPlace(null);
    };

    // Only render if we have a pin location (naming step)
    if (!pinLocation) {
        return null;
    }

    // NAMING STEP - "Add a place" form
    return (
        <Box
            dir={isRTL ? 'rtl' : 'ltr'}
            sx={{
                bgcolor: 'white',
                borderRadius: '12px',
                width: 480,
                height: 720,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: 2,
                    borderBottom: '1px solid #f0f0f0',
                }}
            >
                <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
                    {t('add_place') || 'Add a place'}
                </Typography>
            </Box>

            {/* Content */}
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2, flex: 1, overflow: 'hidden' }}>
                {/* Place name input */}
                <Box sx={{ flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 14, color: '#666', mb: 0.5, width: 'fit-content' }}>
                        {t('place_name') || 'Place name'}*
                    </Typography>
                    <TextField
                        fullWidth
                        value={placeName}
                        onChange={(e) => { if (e.target.value.length <= 50) setPlaceName(e.target.value); }}
                        inputProps={{ maxLength: 50 }}
                        variant="standard"
                        placeholder=""
                        autoComplete="off"
                        spellCheck={false}
                        sx={{
                            '& .MuiInput-root': {
                                fontSize: 16,
                                fontWeight: 500,
                                '&:before': {
                                    borderBottom: '1px solid #e0e0e0',
                                },
                                '&:after': {
                                    borderBottom: `2px solid ${THEME_COLOR}`,
                                },
                            },
                            '& .MuiInput-input': {
                                py: 0.5,
                            },
                        }}
                    />
                </Box>

                {/* Description textarea */}
                <Box sx={{ flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 14, color: '#666', mb: 0.5, width: 'fit-content' }}>
                        {t('description') || 'Description'}
                    </Typography>
                    <TextField
                        fullWidth
                        value={description}
                        onChange={(e) => {
                            if (e.target.value.length <= MAX_DESCRIPTION_LENGTH) {
                                setDescription(e.target.value);
                            }
                        }}
                        variant="standard"
                        autoComplete="off"
                        spellCheck={false}
                        multiline
                        rows={2}
                        sx={{
                            '& .MuiInput-root': {
                                fontSize: 14,
                                '&:before': {
                                    borderBottom: '1px solid #e0e0e0',
                                },
                                '&:after': {
                                    borderBottom: `2px solid ${THEME_COLOR}`,
                                },
                            },
                            '& .MuiInput-input': {
                                py: 0.5,
                            },
                        }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <Typography sx={{ fontSize: 12, color: '#999' }}>
                            {description.length}/{MAX_DESCRIPTION_LENGTH}
                        </Typography>
                    </Box>
                </Box>

                {/* Coordinates */}
                {/* <Box sx={{ flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 14, color: '#666', mb: 0.5, width: 'fit-content' }}>
                        {t('coordinates') || 'Coordinates'}
                    </Typography>
                    <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#333' }}>
                        {pinLocation!.lat.toFixed(6)}, {pinLocation!.lng.toFixed(6)}
                    </Typography>
                </Box> */}

            </Box>

            {/* Footer */}
            <Box
                sx={{
                    display: 'flex',
                    gap: 2,
                    p: 2,
                    borderTop: '1px solid #f0f0f0',
                }}
            >
                {isEditing && (
                    <Button
                        fullWidth
                        variant="outlined"
                        onClick={handleDeletePlace}
                        sx={{
                            textTransform: 'none',
                            borderRadius: '8px',
                            py: 1,
                            borderColor: '#48526B',
                            '&:hover': { borderColor: '#3a4259', bgcolor: 'rgba(72,82,107,0.08)' },
                        }}
                    >
                        {t('delete') || 'Delete'}
                    </Button>
                )}
                <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSavePlace}
                    disabled={!placeName.trim()}
                    sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        py: 1,
                        bgcolor: THEME_COLOR,
                        '&:hover': { bgcolor: '#B84620' },
                        '&.Mui-disabled': { bgcolor: '#ffccbc', color: 'white' },
                    }}
                >
                    {t('save_place') || 'Save Place'}
                </Button>
            </Box>
        </Box>
    );
};

export default DesktopDropAPinContent;
